/**
 * Reports overview, targets and campaigns.
 *
 * Forecast and Territories used to live here too (`docs/html/reports.html`
 * ported flat) — they're now real reports under the Reports submenu
 * (`ReportDetailPages.jsx`), reached via `navModel.js`'s nested `reports`
 * item, rather than fixture-backed pages of their own. Targets stays here:
 * it's a CRUD screen (create/edit a quota), not a report — see
 * `reports.model.md` §6.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getTargets } from '../../api/crm.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { isMock } from '../../api/mockFallback.js';
import { useAuth } from '../../hooks/useAuth.js';
import { BarChart, LineChart, DonutChart } from '../../components/charts/index.jsx';
import {
  PageHeader, Card, Badge, Meter, ErrorState, MockBadge, SkeletonChart, SkeletonTable, SkeletonCard,
} from '../../components/common/index.jsx';
import { money, number } from '../../utils/formatters.js';
import {
  useCampaigns, useCreateCampaign, useUpdateCampaign, useDeleteCampaign,
} from '../campaigns/useCampaigns.js';
import { toRow as toCampaignRow, roiLabel } from '../campaigns/campaignShape.js';
import { CampaignFormModal, DeleteCampaignModal, CampaignMembersModal } from '../campaigns/CampaignModals.jsx';
import { useLeadsReport, useRevenueReport } from './useReports.js';

/* -------------------------------------------------------------------------- */
/* Table shapes                                                                */
/* -------------------------------------------------------------------------- */

const TARGET_COLUMNS = [
  { key: 'owner', header: 'Owner' }, { key: 'period', header: 'Period' },
  { key: 'quota', header: 'Quota' }, { key: 'attained', header: 'Attained' },
  { key: 'progress', header: 'Progress', width: 200 }, { key: 'gap', header: 'Gap' },
];

const CAMPAIGN_COLUMNS = [
  { key: 'campaign', header: 'Campaign' }, { key: 'type', header: 'Type' },
  { key: 'status', header: 'Status' }, { key: 'budget', header: 'Budget' },
  { key: 'spent', header: 'Spent' }, { key: 'leads', header: 'Leads' },
  { key: 'converted', header: 'Converted' }, { key: 'roi', header: 'ROI' },
];

/** The other five reports, as quick-link cards off the overview. */
const REPORT_LINKS = [
  { to: '/reports/leads', icon: 'bi-magnet-fill', label: 'Leads', hint: 'Source, owner, territory, conversion, aging' },
  { to: '/reports/opportunities', icon: 'bi-briefcase-fill', label: 'Opportunities', hint: 'Pipeline, aging, win/loss, sales cycle' },
  { to: '/reports/activities', icon: 'bi-list-check', label: 'Activities', hint: 'Calls, meetings, emails, by salesperson' },
  { to: '/reports/revenue', icon: 'bi-cash-coin', label: 'Revenue', hint: 'By salesperson, territory, product, customer' },
  { to: '/reports/forecast', icon: 'bi-graph-up-arrow', label: 'Forecast', hint: 'Target vs. closed, commit, accuracy' },
  { to: '/reports/territories', icon: 'bi-globe-americas', label: 'Territories', hint: 'Leads, pipeline and win rate by region' },
];

/* -------------------------------------------------------------------------- */
/* Reports overview                                                            */
/* -------------------------------------------------------------------------- */

/** The landing report: a preview of the two most-used cuts, quick links to the rest, and campaign return. */
export function ReportsPage() {
  const leadsBySource = useLeadsReport({ breakdown: 'source' });
  const revenueTrend = useRevenueReport({ breakdown: 'owner' });
  const campaigns = useCampaigns({ limit: 50 });
  const campaignRows = (campaigns.data?.data ?? []).map(toCampaignRow);

  return (
    <>
      <PageHeader
        title="Reports & analytics"
        subtitle="Leads, revenue and campaign performance — see the Reports menu for every cut."
        badge={isMock(leadsBySource.data) ? <MockBadge section="reports" /> : null}
      />

      <div className="row g-2 mb-3">
        {REPORT_LINKS.map((r) => (
          <div className="col-6 col-lg-2" key={r.to}>
            <Link to={r.to} className="card-x hoverable p-3 h-100 d-block text-decoration-none">
              <i className={`bi ${r.icon} t-lg`} style={{ color: 'var(--brand-500)' }} aria-hidden="true" />
              <div className="fw-600 t-sm mt-2">{r.label}</div>
              <div className="t-2xs ink-3 mt-1">{r.hint}</div>
            </Link>
          </div>
        ))}
      </div>

      <div className="grid-2 mb-3">
        <Card title="Leads by source" subtitle="Most recent 12 months">
          {leadsBySource.isLoading ? <SkeletonChart height={240} /> : (
            <BarChart
              data={leadsBySource.data?.rows ?? []}
              xKey="label"
              series={[{ key: 'count', label: 'Leads' }]}
              horizontal
              height={240}
            />
          )}
        </Card>

        <Card title="Revenue trend" subtitle="Closed-won, by month">
          {revenueTrend.isLoading ? <SkeletonChart height={240} /> : (
            <LineChart
              data={revenueTrend.data?.trend ?? []}
              xKey="label"
              series={[{ key: 'value', label: 'Revenue' }]}
              money
              height={240}
            />
          )}
        </Card>
      </div>

      <Card title="Campaign performance" subtitle="Spend, leads generated and return">
        {campaigns.isLoading ? <SkeletonTable rows={5} columns={CAMPAIGN_COLUMNS} /> : <CampaignTable rows={campaignRows} />}
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Targets                                                                     */
/* -------------------------------------------------------------------------- */

/** Quota attainment per person, ordered worst first so coaching targets surface. */
export function TargetsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.targets.list({}),
    queryFn: () => getTargets(),
  });

  const rows = [...(data?.data ?? [])].sort((a, b) => a.pct - b.pct);

  return (
    <>
      <PageHeader
        title="Targets & quotas"
        subtitle="Attainment this quarter, furthest behind first."
        badge={isMock(data) ? <MockBadge section="targets" /> : null}
      />
      <Card>
        {isLoading ? <SkeletonTable rows={6} columns={TARGET_COLUMNS} /> : error ? <ErrorState error={error} onRetry={refetch} /> : (
          <div className="table-x-wrap">
            <table className="table-x">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Period</th>
                  <th className="text-end">Quota</th>
                  <th className="text-end">Attained</th>
                  <th style={{ width: 200 }}>Progress</th>
                  <th className="text-end">Gap</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.owner}-${row.period}`}>
                    <td className="fw-600">{row.owner}</td>
                    <td>{row.period}</td>
                    <td className="text-end">{money(row.quota)}</td>
                    <td className="text-end">{money(row.attained)}</td>
                    <td>
                      <Meter value={row.pct} tone={row.pct >= 90 ? 'emerald' : row.pct >= 65 ? 'brand' : 'rose'} showValue />
                    </td>
                    <td className="text-end">
                      {row.attained >= row.quota
                        ? <span className="text-success">Met</span>
                        : <span className="text-danger">{money(row.quota - row.attained)}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Campaigns                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Marketing spend against leads generated and deals won — now the real §9
 * module (`crm.campaign`), not the fixture-shaped `{type, spent, leads, roi}`
 * row this screen read before that module existed. `roi` used to be a fake
 * small multiplier ("3.4×"); the real `performance.roi_pct` is a live-computed
 * percentage return (see `campaignShape.roiLabel` and
 * `campaigns.model.md`), so this screen also owns creating and managing
 * campaigns now — there was nowhere else to put it, and a read-only report is
 * a strange place to stop once the write side exists.
 */
export function CampaignsPage() {
  const { can } = useAuth();
  const canCreate = can('campaigns', 'create');
  const canEdit = can('campaigns', 'edit');
  const canDelete = can('campaigns', 'delete');

  const { data, isLoading, error, refetch } = useCampaigns({ limit: 100 });
  const rows = (data?.data ?? []).map(toCampaignRow);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [viewingMembers, setViewingMembers] = useState(null);

  const createMutation = useCreateCampaign();
  const updateMutation = useUpdateCampaign();
  const deleteMutation = useDeleteCampaign();

  return (
    <>
      <PageHeader
        title="Campaigns"
        subtitle="Spend, leads generated and return."
        badge={isMock(data) ? <MockBadge section="campaigns" /> : null}
      >
        {canCreate && (
          <button type="button" className="btn btn-add btn-sm-x" onClick={() => { setEditing(null); setFormOpen(true); }}>
            <i className="bi bi-plus-lg me-1" aria-hidden="true" />New Campaign
          </button>
        )}
      </PageHeader>
      {isLoading ? (
        <>
          <div className="grid-2 mb-3"><SkeletonCard height={240} /><SkeletonCard height={240} /></div>
          <SkeletonTable rows={5} columns={CAMPAIGN_COLUMNS} />
        </>
      ) : error ? <ErrorState error={error} onRetry={refetch} /> : (
        <>
          <div className="grid-2 mb-3">
            <Card title="Leads by campaign">
              <DonutChart
                data={rows.map((c) => ({ label: c.name, value: c.leads }))}
                height={230}
                centerValue={number(rows.reduce((s, c) => s + c.leads, 0))}
                centerLabel="Leads"
              />
            </Card>
            <Card title="Return on spend" subtitle="Percentage return — (won value − spend) ÷ spend">
              <BarChart
                data={rows.map((c) => ({ ...c, roiChart: c.roiPct ?? 0 }))}
                xKey="name"
                series={[{ key: 'roiChart', label: 'ROI (%)' }]}
                horizontal
                height={230}
              />
            </Card>
          </div>
          <Card title="Detail">
            <CampaignTable
              rows={rows}
              onEdit={canEdit ? (row) => { setEditing(row); setFormOpen(true); } : null}
              onMembers={canEdit ? setViewingMembers : null}
              onDelete={canDelete ? setDeleting : null}
            />
          </Card>
        </>
      )}

      <CampaignFormModal
        open={formOpen}
        campaign={editing}
        busy={createMutation.isPending || updateMutation.isPending}
        onClose={() => setFormOpen(false)}
        onSubmit={async (payload) => {
          if (editing) {
            await updateMutation.mutateAsync({ id: editing.id, patch: payload, rowVersion: editing.rowVersion });
          } else {
            await createMutation.mutateAsync(payload);
          }
          setFormOpen(false);
        }}
      />

      <DeleteCampaignModal
        open={Boolean(deleting)}
        campaign={deleting}
        busy={deleteMutation.isPending}
        onClose={() => setDeleting(null)}
        onConfirm={async (reason) => {
          await deleteMutation.mutateAsync({ id: deleting.id, reason, rowVersion: deleting.rowVersion });
          setDeleting(null);
        }}
      />

      <CampaignMembersModal
        open={Boolean(viewingMembers)}
        campaign={viewingMembers}
        onClose={() => setViewingMembers(null)}
      />
    </>
  );
}

/**
 * Shared campaign table — used by the overview (no `onEdit`/`onMembers`/
 * `onDelete`, so no actions column) and the campaigns screen (all three).
 */
function CampaignTable({ rows, onEdit, onMembers, onDelete }) {
  const showActions = Boolean(onEdit || onMembers || onDelete);
  return (
    <div className="table-x-wrap">
      <table className="table-x">
        <thead>
          <tr>
            <th>Campaign</th>
            <th>Type</th>
            <th>Status</th>
            <th className="text-end">Budget</th>
            <th className="text-end">Spent</th>
            <th className="text-end">Leads</th>
            <th className="text-end">Converted</th>
            <th className="text-end">ROI</th>
            {showActions && <th />}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id}>
              <td className="fw-600">
                {onEdit ? <a href={`#${c.id}`} onClick={(e) => { e.preventDefault(); onEdit(c); }}>{c.name}</a> : c.name}
              </td>
              <td>{c.type ?? '—'}</td>
              <td><Badge tone={c.statusTone}>{c.statusLabel}</Badge></td>
              <td className="text-end">{c.budget != null ? money(c.budget, c.currency) : '—'}</td>
              <td className="text-end">{c.spent != null ? money(c.spent, c.currency) : '—'}</td>
              <td className="text-end">{number(c.leads)}</td>
              <td className="text-end">{number(c.converted)}</td>
              {/* Negative means the campaign is losing money — worth colouring; `null` (no spend yet) is neither. */}
              <td className={`text-end fw-600 ${c.roiPct == null ? '' : c.roiPct < 0 ? 'text-danger' : 'text-success'}`}>
                {roiLabel(c.roiPct)}
              </td>
              {showActions && (
                <td className="text-end row-actions">
                  <div className="d-inline-flex gap-1">
                    {onMembers && (
                      <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Members" onClick={() => onMembers(c)}>
                        <i className="bi bi-people" aria-hidden="true" />
                      </button>
                    )}
                    {onDelete && (
                      <button type="button" className="icon-btn" style={{ width: 28, height: 28, fontSize: 13 }} title="Delete" onClick={() => onDelete(c)}>
                        <i className="bi bi-trash" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
